/**
 * URL param (de)serialization for recipe filters.
 *
 * Filters are shareable/SSR-friendly/back-button-safe by living in the URL.
 * Unknown or malformed values are silently ignored — never throws.
 */

export const DIETARY_KEYS = [
  'vegan',
  'vegetarian',
  'glutenFree',
  'dairyFree',
  'hashimoto',
] as const;

export type DietaryKey = (typeof DIETARY_KEYS)[number];

/**
 * URL marker meaning "the user explicitly cleared the filters" (#614).
 *
 * Needed because a profile dietary preference seeds the browse filter, and
 * without a marker an empty `dietary` param is ambiguous: it could be a first
 * landing (apply the default) or a deliberate clear (do not). Absent the
 * distinction, "Clear all" would silently re-apply the preference and the
 * filter could never be turned off.
 */
export const NO_DEFAULT_PARAM = 'nodefault';

/**
 * Map profile dietary-preference slugs onto browse filter keys.
 *
 * The two vocabularies differ on purpose and are not interchangeable: profile
 * slugs are kebab-case rows in `dietary_preferences` and cover preferences the
 * recipe schema has no column for (keto, paleo, low-carb, nut-free). Only the
 * ones backed by a real recipe flag can filter, so anything unmapped is
 * dropped rather than guessed at.
 */
const PREFERENCE_SLUG_TO_DIETARY_KEY: Record<string, DietaryKey> = {
  vegan: 'vegan',
  vegetarian: 'vegetarian',
  'gluten-free': 'glutenFree',
  'dairy-free': 'dairyFree',
  hashimoto: 'hashimoto',
};

export function dietaryKeysFromPreferenceSlugs(slugs: string[]): DietaryKey[] {
  const keys = slugs
    .map((slug) => PREFERENCE_SLUG_TO_DIETARY_KEY[slug])
    .filter((k): k is DietaryKey => Boolean(k));
  return Array.from(new Set(keys));
}

/** Variant tiers, single-select. Matched against ANY feasible variant of a recipe. */
export const VARIANT_KEYS = ['quick', 'classic', 'bougie'] as const;
export type VariantKey = (typeof VARIANT_KEYS)[number];

/**
 * "Max total time" ceiling presets (minutes) — matched against a variant's
 * `prep + cook` total. Any value in (0, 1440] is accepted from the URL; these
 * are just the chips the filter panel offers.
 */
export const COOK_TIME_PRESETS = [20, 30, 45, 60] as const;

export const SORT_KEYS = [
  'relevance',
  'newest',
  'trending',
  'fastest',
  'calories', // by calories per serving
] as const;
export type SortKey = (typeof SORT_KEYS)[number];

export const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

/**
 * The "natural" direction for each sort — what the dropdown label implies
 * out of the box. Newest = most recent first (DESC). Fastest = lowest time
 * first (ASC). Calories = lowest first (ASC). The user can flip any of
 * these via ?dir= on the URL; the toggle icon in SortDropdown writes the
 * override.
 */
const NATURAL_SORT_DIR: Record<SortKey, SortDirection> = {
  relevance: 'desc',
  newest:    'desc',
  trending:  'desc',
  fastest:   'asc',
  calories:  'asc',
};

export function naturalSortDir(sort: SortKey): SortDirection {
  return NATURAL_SORT_DIR[sort];
}

/** Resolve the effective direction: explicit `dir` overrides natural. */
export function effectiveSortDir(
  sort: SortKey,
  dir: SortDirection | null,
): SortDirection {
  return dir ?? NATURAL_SORT_DIR[sort];
}

/**
 * Whether a sort meaningfully supports a direction toggle. `relevance` is
 * always best-match-first; flipping it would just surface noise.
 */
export function sortSupportsDirection(sort: SortKey): boolean {
  return sort !== 'relevance';
}

export interface RecipeFilters {
  dietary: DietaryKey[];
  cuisineIds: string[];
  categoryIds: string[];
  /** Single variant tier the recipe must have a feasible variant for. null = any. */
  variant: VariantKey | null;
  /** Max total (prep + cook) minutes on any feasible variant. null = no cap. */
  maxCookTime: number | null;
  /**
   * Per-user library filters. Only meaningful for a signed-in viewer — the
   * feed layer ignores them when there's no authenticated user, and they force
   * a cache-bypassing user-scoped query path (the shared discovery cache is
   * user-agnostic). `cooked` = the viewer has cooked it; `cookbook` = it's in
   * one of the viewer's cookbooks.
   */
  cooked: boolean;
  cookbook: boolean;
}

export const EMPTY_FILTERS: RecipeFilters = {
  dietary: [],
  cuisineIds: [],
  categoryIds: [],
  variant: null,
  maxCookTime: null,
  cooked: false,
  cookbook: false,
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function splitCsv(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function parseRecipeFilters(
  searchParams: Record<string, string | undefined>,
): RecipeFilters {
  const dietaryRaw = splitCsv(searchParams.dietary);
  const dietary = dietaryRaw.filter((k): k is DietaryKey =>
    (DIETARY_KEYS as readonly string[]).includes(k),
  );

  const cuisineIds = splitCsv(searchParams.cuisine).filter((id) =>
    UUID_REGEX.test(id),
  );
  const categoryIds = splitCsv(searchParams.category).filter((id) =>
    UUID_REGEX.test(id),
  );

  const variant: VariantKey | null =
    searchParams.variant &&
    (VARIANT_KEYS as readonly string[]).includes(searchParams.variant)
      ? (searchParams.variant as VariantKey)
      : null;

  let maxCookTime: number | null = null;
  if (searchParams.maxCookTime) {
    const n = Number(searchParams.maxCookTime);
    if (Number.isFinite(n) && n > 0 && n <= 1440) {
      maxCookTime = Math.floor(n);
    }
  }

  // De-duplicate array filters
  return {
    dietary: Array.from(new Set(dietary)),
    cuisineIds: Array.from(new Set(cuisineIds)),
    categoryIds: Array.from(new Set(categoryIds)),
    variant,
    maxCookTime,
    cooked: isTruthyParam(searchParams.cooked),
    cookbook: isTruthyParam(searchParams.cookbook),
  };
}

/** A URL flag is "on" when present as `1` or `true` (case-insensitive). */
function isTruthyParam(value: string | undefined): boolean {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return v === '1' || v === 'true';
}

/**
 * Serialize filters into a URL query string (without leading "?").
 * Preserves any extra params passed via `extra` (e.g. `q`, `sort` on /recipes).
 */
export function serializeRecipeFilters(
  filters: RecipeFilters,
  extra: Record<string, string | undefined> = {},
): string {
  const params = new URLSearchParams();

  // Preserve extra params first so filter params don't overwrite them
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== '') params.set(k, v);
  }

  if (filters.dietary.length > 0) params.set('dietary', filters.dietary.join(','));
  if (filters.cuisineIds.length > 0) params.set('cuisine', filters.cuisineIds.join(','));
  if (filters.categoryIds.length > 0) params.set('category', filters.categoryIds.join(','));
  if (filters.variant !== null) params.set('variant', filters.variant);
  if (filters.maxCookTime !== null) params.set('maxCookTime', String(filters.maxCookTime));
  if (filters.cooked) params.set('cooked', '1');
  if (filters.cookbook) params.set('cookbook', '1');

  return params.toString();
}

/**
 * Parse the optional `sort` URL param. Returns null when absent or invalid so
 * callers can decide on a context-aware default (relevance with a query,
 * otherwise no override).
 */
export function parseSortKey(raw: string | undefined): SortKey | null {
  if (!raw) return null;
  return (SORT_KEYS as readonly string[]).includes(raw) ? (raw as SortKey) : null;
}

/** Parse the optional `dir` URL param. Returns null when absent or invalid. */
export function parseSortDir(raw: string | undefined): SortDirection | null {
  if (!raw) return null;
  return (SORT_DIRECTIONS as readonly string[]).includes(raw)
    ? (raw as SortDirection)
    : null;
}

/** True if any filter is non-default. */
export function hasActiveFilters(filters: RecipeFilters): boolean {
  return (
    filters.dietary.length > 0 ||
    filters.cuisineIds.length > 0 ||
    filters.categoryIds.length > 0 ||
    filters.variant !== null ||
    filters.maxCookTime !== null ||
    filters.cooked ||
    filters.cookbook
  );
}

/** Count of active filter selections (used for the toggle badge). */
export function countActiveFilters(filters: RecipeFilters): number {
  return (
    filters.dietary.length +
    filters.cuisineIds.length +
    filters.categoryIds.length +
    (filters.variant !== null ? 1 : 0) +
    (filters.maxCookTime !== null ? 1 : 0) +
    (filters.cooked ? 1 : 0) +
    (filters.cookbook ? 1 : 0)
  );
}
